import config from '../../config/environment';

// Model domenowy dla produktu
export class Product {
  constructor(data) {
    this.id = data.id;
    this.externalId = data.externalId;
    this.purchasePrice = data.purchasePrice;
    this.name = data.name;
    this.imageBase64 = data.imageBase64; // Zachowujemy dla kompatybilności wstecznej
    this.image = data.image; // Nowa struktura obrazka
    this.imageId = data.imageId; // Nowe pole imageId
    this.quantity = data.quantity || 0;
    this.enabledSet = data.enabledSet || [];
    this.status = this.calculateStatus();
  }

  // Logika biznesowa - obliczanie statusu produktu
  calculateStatus() {
    if (this.quantity === 0) return 'out_of_stock';
    if (this.quantity <= 5) return 'low_stock';
    return 'available';
  }

  // Logika biznesowa - sprawdzanie czy produkt wymaga uwagi
  needsAttention() {
    return this.status === 'out_of_stock' || this.status === 'low_stock';
  }

  // Logika biznesowa - aktualizacja stanu magazynowego
  updateQuantity(newQuantity) {
    this.quantity = newQuantity;
    this.status = this.calculateStatus();
  }

  // Logika biznesowa - pobieranie liczby aktywnych sklepów
  getActiveShopsCount() {
    return this.enabledSet.filter(shop => shop.enabled).length;
  }

  // Logika biznesowa - sprawdzanie czy produkt jest aktywny w sklepie
  isEnabledInShop(shopId) {
    const shop = this.enabledSet.find(s => s.shopId === shopId);
    return shop ? shop.enabled : false;
  }

  // Logika biznesowa - włączanie/wyłączanie produktu w sklepie
  toggleShopEnabled(shopId, enabled) {
    const shop = this.enabledSet.find(s => s.shopId === shopId);
    if (shop) {
      shop.enabled = enabled;
    } else {
      this.enabledSet.push({
        shopId,
        shopName: `Sklep ${shopId}`,
        enabled
      });
    }
  }

  // Logika biznesowa - pobieranie obrazka z optymalizacją
  getImageUrl() {
    if (this.imageBase64) {
      return `data:image/jpeg;base64,${this.imageBase64}`;
    }
    if (this.image && this.image.data) {
      // Konwertuj byte array na base64
      const base64 = btoa(String.fromCharCode(...this.image.data));
      return `data:${this.image.contentType || 'image/jpeg'};base64,${base64}`;
    }
    // Nowa struktura - obrazek pobierany przez imageId
    if (this.imageId) {
      // Zwróć null - obrazek będzie pobierany przez LazyImage z autoryzacją
      return null;
    }
    return null;
  }

  // Metoda do pobierania obrazka przez API z autoryzacją
  async getImageBlob() {
    if (this.imageId) {
      try {
        const { allegroApiService } = await import('../../shared/services/allegroApi');
        return await allegroApiService.getProductImage(this.imageId);
      } catch (error) {
        console.error('Failed to fetch product image:', error);
        return null;
      }
    }
    return null;
  }

    // Optymalizacja obrazka - zmniejszenie rozmiaru z użyciem synchronicznej metody
  static optimizeImage(base64String, maxWidth = 150, maxHeight = 150, quality = 0.7) {
    return new Promise((resolve, reject) => {
      // Użyj synchronicznej optymalizacji (działa we wszystkich przeglądarkach)
      Product.optimizeImageSync(base64String, maxWidth, maxHeight, quality)
        .then(resolve)
        .catch(reject);
    });
  }

  // Synchroniczna optymalizacja obrazka (fallback)
  static optimizeImageSync(base64String, maxWidth = 150, maxHeight = 150, quality = 0.7) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          // Oblicz nowe wymiary zachowując proporcje
          let { width, height } = img;
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Rysuj zoptymalizowany obrazek
          ctx.drawImage(img, 0, 0, width, height);
          
          // Konwertuj do base64 z kompresją
          const optimizedBase64 = canvas.toDataURL('image/jpeg', quality);
          const base64Data = optimizedBase64.split(',')[1];
          
          resolve(base64Data);
        } catch (error) {
          console.error('Sync optimization error:', error);
          reject(error);
        }
      };
      
      img.onerror = (error) => {
        console.error('Failed to load image for sync optimization:', error);
        reject(new Error('Failed to load image for optimization'));
      };
      
      img.src = `data:image/jpeg;base64,${base64String}`;
    });
  }

  // Factory method do tworzenia produktu z danych API
  static fromApi(data) {
    return new Product(data);
  }

  // Metoda do serializacji do API
  toApi() {
    const apiData = {
      id: this.id,
      externalId: this.externalId,
      purchasePrice: this.purchasePrice,
      name: this.name,
      imageId: this.imageId, // Używamy imageId zamiast image
      quantity: this.quantity,
    };
    
    return apiData;
  }

  // Metoda do tworzenia nowego produktu
  static createNew(productData) {
    return new Product({
      id: null,
      externalId: productData.externalId,
      purchasePrice: productData.purchasePrice,
      name: productData.name,
      imageId: productData.imageId, // Używamy imageId zamiast image
      quantity: productData.quantity || 0,
      enabledSet: [],
    });
  }
} 